import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye, Clock } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import axios from "axios";
import { API } from "../lib/api";
import { useLang } from "../context/LangContext";

// Category config with translation keys
const categoryConfig = [
  { name: 'Gündem', key: 'gundem', icon: '🔥', color: 'text-red-400', bg: 'bg-red-500/10' },
  { name: 'Ekonomi', key: 'ekonomi', icon: '📈', color: 'text-green-400', bg: 'bg-green-500/10' },
  { name: 'Spor', key: 'spor', icon: '⚽', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { name: 'Teknoloji', key: 'teknoloji', icon: '💻', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { name: 'Dünya', key: 'dunya', icon: '🌍', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  { name: 'Sağlık', key: 'saglik', icon: '🏥', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
];

const MobileCategoryBar = ({ selectedCategory }) => {
  const scrollRef = useRef(null);
  const { t } = useLang();

  return (
    <div className="lg:hidden mb-4 -mx-4 px-4" data-testid="mobile-category-bar">
      <div 
        ref={scrollRef}
        className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 snap-x"
      >
        <Link
          to="/"
          className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all snap-start ${
            !selectedCategory 
              ? 'bg-amber-500 text-black' 
              : 'bg-zinc-800 text-zinc-400'
          }`}
        >
          {t('allNews')}
        </Link>
        {categoryConfig.map((cat) => (
          <Link
            key={cat.name}
            to={`/?category=${encodeURIComponent(cat.name)}`}
            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all snap-start ${
              selectedCategory === cat.name 
                ? `${cat.bg} ${cat.color}` 
                : 'bg-zinc-800 text-zinc-400'
            }`}
          >
            <span>{cat.icon}</span>
            <span>{t(cat.key)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const MobileHeroSlider = ({ news }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const sliderRef = useRef(null);

  const minSwipeDistance = 50;
  const featuredNews = news?.slice(0, 5) || [];

  useEffect(() => {
    if (featuredNews.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % featuredNews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [featuredNews.length]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      setCurrentIndex(prev => (prev + 1) % featuredNews.length);
    } else if (isRightSwipe) {
      setCurrentIndex(prev => (prev - 1 + featuredNews.length) % featuredNews.length);
    }
  };

  if (!featuredNews.length) return null;

  return (
    <div 
      className="lg:hidden relative mb-6 -mx-4"
      ref={sliderRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-testid="mobile-hero-slider"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {featuredNews.map((item, index) => (
          <Link
            key={item.id}
            to={`/haber/${item.slug || item.id}`}
            className={`absolute inset-0 transition-all duration-500 ${
              index === currentIndex 
                ? 'opacity-100 scale-100' 
                : 'opacity-0 scale-105'
            }`}
          >
            <img 
              src={item.image_url}
              alt={item.title}
              className="w-full h-full object-cover"
              onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800'}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <span className="inline-block px-2 py-0.5 bg-amber-500 text-black text-[10px] font-bold rounded-full mb-2">
                {item.category}
              </span>
              <h2 className="text-lg font-bold text-white leading-snug line-clamp-2 mb-1">
                {item.title}
              </h2>
              <div className="flex items-center gap-3 text-zinc-400 text-xs">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(item.created_at), "d MMM", { locale: tr })}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {item.views}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Dots */}
      <div className="absolute bottom-2 right-4 flex items-center gap-1.5">
        {featuredNews.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === currentIndex ? 'w-4 bg-amber-500' : 'w-1.5 bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const MobileNewsCard = ({ item, featured = false }) => {
  if (featured) {
    return (
      <Link
        to={`/haber/${item.slug || item.id}`}
        className="group block bg-zinc-900/50 rounded-xl overflow-hidden border border-zinc-800/50 touch-feedback"
        data-testid={`mobile-news-card-${item.id}`}
      >
        <div className="aspect-video relative overflow-hidden">
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400'}
          />
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 bg-amber-500 text-black text-[10px] font-bold rounded-full">
              {item.category}
            </span>
          </div>
        </div>
        <div className="p-3">
          <h3 className="text-sm font-semibold text-white line-clamp-2 leading-snug mb-2">
            {item.title}
          </h3>
          <div className="flex items-center justify-between text-[10px] text-zinc-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {format(new Date(item.created_at), "d MMM", { locale: tr })}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {item.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/haber/${item.slug || item.id}`}
      className="flex items-center gap-3 p-2 bg-zinc-900/30 rounded-xl touch-feedback"
      data-testid={`mobile-news-card-${item.id}`}
    >
      <img
        src={item.image_url}
        alt={item.title}
        className="w-20 h-14 rounded-lg object-cover flex-shrink-0"
        loading="lazy"
        onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200'}
      />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-white line-clamp-2 leading-snug">
          {item.title}
        </h3>
        <p className="text-[10px] text-zinc-500 mt-1">{item.category}</p>
      </div>
    </Link>
  );
};

const MobileCategorySection = ({ category, icon, color, translatedCategory }) => {
  const [news, setNews] = useState([]);
  const scrollRef = useRef(null);
  const { lang, t } = useLang();

  // Use translated category name for display
  const displayCategory = translatedCategory || category;

  useEffect(() => {
    axios.get(`${API}/news?category=${encodeURIComponent(category)}&limit=6&language=${lang}`)
      .then(r => setNews(r.data))
      .catch(() => {});
  }, [category, lang]);

  if (news.length === 0) return null;

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="lg:hidden mb-6" data-testid={`mobile-cat-section-${category.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <h3 className={`text-base font-bold ${color}`}>{displayCategory}</h3>
        </div>
        <Link 
          to={`/?category=${encodeURIComponent(category)}`}
          className="text-xs text-zinc-500 hover:text-amber-400"
        >
          {t('allNews')} →
        </Link>
      </div>
      
      <div className="relative">
        <div 
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide snap-x pb-2"
        >
          {news.map((item) => (
            <Link
              key={item.id}
              to={`/haber/${item.slug || item.id}`}
              className="flex-shrink-0 w-[160px] snap-start touch-feedback"
            >
              <div className="aspect-video rounded-xl overflow-hidden mb-2">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=300'}
                />
              </div>
              <h4 className="text-xs font-medium text-white line-clamp-2 leading-snug">
                {item.title}
              </h4>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export { 
  MobileCategoryBar, 
  MobileHeroSlider, 
  MobileNewsCard, 
  MobileCategorySection,
  categoryConfig 
};
